import { useCallback, useEffect, useState } from 'react';

import useBuyNowCart from '@/Application/Selectors/buyNow/useBuyNowCart';
import { useBuyNowSkuHistory } from '@/Application/Selectors/buyNow/useBuyNowSkuHistory';
import { store } from '@/Infrastructure/State';
import { getShippingGrouping } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import { extractCartItemSkus } from '@/modules/shared/utils/getCartSkus';
import type { ShippingMethod } from '@/modules/shippingMethod/domain/ShippingMethod';
import type { shippingMethodBodyRequest } from '@/modules/shippingMethod/domain/ShippingMethodRepository';
import useBuyNowShippingGrouping from '@/sections/shippingMethods/hooks/useBuyNowShippingGrouping';
import { shippingMethodDtoToShippingMethod } from '@/sections/shippingMethods/mappers/shippingMethodMapper';

const useGetShippingGrouping = () => {
  const { data: cart } = useBuyNowCart();
  const { skuHistory } = useBuyNowSkuHistory();
  const { data: results, isLoading, error } = useBuyNowShippingGrouping();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod | null>(
    null
  );
  const [isError, setIsError] = useState<boolean>(Boolean(error));

  const getShippingMethods = useCallback(
    async (params?: shippingMethodBodyRequest[]) => {
      if (!cart?.id) {
        return null;
      }

      const cartSkus = extractCartItemSkus(cart);
      const derivedParams = params ?? cartSkus;
      const { payload } = await store.dispatch(
        getShippingGrouping({ params: derivedParams, cartId: cart.id })
      );

      if (payload) {
        setIsError(false);
      }
      return payload;
    },
    [cart]
  );

  useEffect(() => {
    if (results && cart) {
      const skuHistoryArray = skuHistory ? [skuHistory] : [];
      const shippingMethod = shippingMethodDtoToShippingMethod(
        results,
        cart,
        skuHistoryArray
      );
      setShippingMethods(shippingMethod);
      if (isInitialLoad && shippingMethod?.deliveryGroups?.length) {
        setIsInitialLoad(false);
      }
    }
  }, [results, cart, isInitialLoad, skuHistory]);

  useEffect(() => {
    if (error) {
      setIsError(true);
    }
  }, [error]);

  return {
    results,
    unavailable: results?.unavailable,
    refetchShippingMethods: getShippingMethods,
    shippingMethods,
    isLoading,
    isInitialLoading: isInitialLoad,
    isError,
    error
  };
};

export default useGetShippingGrouping;
